/**
 * Hub alert setup: which devices count as ready for order alerts and what the operator still has
 * to do. Pure rules (no browser APIs), so every combination is unit-tested.
 *
 * Ready = Chrome or Edge on Android, Windows or macOS, running as the installed Hub app,
 * notifications allowed, a push subscription on this device, and a sound test the operator
 * confirmed on this device. Plan: handoff/2026-10-13-hub-alert-setup-and-update-plan.md.
 */

export type HubAlertSetupMode = 'off' | 'soft' | 'hard';

/**
 * Rollout switch. `soft`: a red strip on every Hub page links to the setup page. `hard`: Hub shows
 * the setup page instead of any page until this device is ready. One deploy changes it.
 */
export const HUB_ALERT_SETUP_MODE: HubAlertSetupMode = 'soft';

/** Local and Stage only: `?hubAlertSetupMode=off|soft|hard` previews another mode (kept per tab). */
export const MODE_OVERRIDE_PARAM = 'hubAlertSetupMode';

export const resolveSetupMode = (
  configured: HubAlertSetupMode,
  override: string | null | undefined,
  overrideAllowed: boolean,
): HubAlertSetupMode =>
  overrideAllowed && (override === 'off' || override === 'soft' || override === 'hard')
    ? override
    : configured;

export type DevicePlatform = 'android' | 'windows' | 'mac' | 'other';

/**
 * Operators who get order alerts, work at a facility, on a platform this setup can guide (Android,
 * Windows, macOS; the wrong browser there still gets the "use Chrome" step). Everyone else (iPhone,
 * Linux, ChromeOS, no facility) keeps HubAlerts' own banner and prompt and is never gated.
 */
export const setupApplies = (
  isRecipient: boolean,
  facilityCount: number,
  platform: DevicePlatform,
) => isRecipient && facilityCount > 0 && platform !== 'other';

export type DeviceBrowser = 'chrome' | 'edge' | 'other';
export type DeviceInfo = { platform: DevicePlatform; browser: DeviceBrowser; supported: boolean };

/** `navigator.userAgentData` (Chromium only). Absent on Safari, Firefox and Chrome for iPhone. */
export type BrowserHints = {
  brands?: ReadonlyArray<{ brand: string }> | null;
  platform?: string | null;
  mobile?: boolean | null;
} | null;

/**
 * Chrome or Edge on Android, Windows or macOS. The browser is trusted only from its own brand list:
 * other Chromium browsers (Samsung Internet, Brave, Opera), Android WebView in-app browsers and
 * Chrome for iPhone either name themselves there or have none. Without a brand list (Safari,
 * Firefox) the platform comes from the user agent, so those browsers still get the "use Chrome"
 * step; a touch-screen "Macintosh" is an iPad.
 */
export const detectDevice = (
  hints: BrowserHints,
  userAgent = '',
  maxTouchPoints = 0,
): DeviceInfo => {
  const brands = (hints?.brands ?? []).map(entry => entry.brand);
  const browser: DeviceBrowser = brands.includes('Microsoft Edge')
    ? 'edge'
    : brands.includes('Google Chrome')
      ? 'chrome'
      : 'other';
  const platformName = hints?.platform ?? '';
  const platform: DevicePlatform = platformName
    ? platformName === 'Android'
      ? 'android'
      : platformName === 'Windows'
        ? 'windows'
        : platformName === 'macOS'
          ? 'mac'
          : 'other'
    : /Android/.test(userAgent)
      ? 'android'
      : /Windows NT/.test(userAgent)
        ? 'windows'
        : /Macintosh/.test(userAgent) && maxTouchPoints <= 1
          ? 'mac'
          : 'other';
  return { platform, browser, supported: browser !== 'other' && platform !== 'other' };
};

/** Launched as the installed app (its own window), not a browser tab. */
export const DISPLAY_MODES_INSTALLED = [
  'standalone',
  'window-controls-overlay',
  'minimal-ui',
] as const;

export type SetupStep = 'browser' | 'install' | 'permission' | 'connect' | 'test';

export type ReadinessInput = {
  device: DeviceInfo;
  installed: boolean;
  permission: NotificationPermission | 'unsupported';
  /** Push subscription on this device: null while the registration is still running. */
  pushReady: boolean | null;
  testPassed: boolean;
};

/**
 * The first thing still missing, in the order the operator fixes it; null = ready.
 *
 * The app needs installing only until this device passes setup: the sound test is only reachable
 * in the installed app, and a browser tab on a set-up device (a Hub link opened in Chrome) shares
 * its permission, push subscription and setup record, so alerts still reach it.
 */
export const pendingStep = (input: ReadinessInput): SetupStep | null => {
  if (!input.device.supported || input.permission === 'unsupported') return 'browser';
  if (!input.installed && !input.testPassed) return 'install';
  if (input.permission !== 'granted') return 'permission';
  if (input.pushReady !== true) return 'connect';
  if (!input.testPassed) return 'test';
  return null;
};

export const SETUP_STEPS: ReadonlyArray<{ step: SetupStep; title: string }> = [
  { step: 'browser', title: 'Use Chrome' },
  { step: 'install', title: 'Install the Hub app' },
  { step: 'permission', title: 'Allow notifications' },
  { step: 'connect', title: 'Connect this device' },
  { step: 'test', title: 'Check the alert sound' },
];

/**
 * Android only: opens a URL in Chrome from another browser or an in-app browser (WhatsApp,
 * Samsung Internet). Elsewhere the operator copies the link.
 */
export const chromeIntentUrl = (url: string) => {
  const parsed = new URL(url);
  return `intent://${parsed.host}${parsed.pathname}${parsed.search}#Intent;scheme=${parsed.protocol.replace(':', '')};package=com.android.chrome;end`;
};

/** How long a missing push subscription counts as "still registering" before it is a failure. */
export const REGISTRATION_GRACE_MS = 8000;

/** The test notification appears after this delay, so a phone can be locked first. */
export const TEST_DELAY_MS = 5000;
/**
 * If the test notification was neither tapped nor closed in any Hub window by then, offer "I didn't
 * get it". Long enough to notice a card in the corner of a computer screen and click it.
 */
export const TEST_MISSED_AFTER_MS = 45000;
